import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import { useAppStore } from "../store/appStore";
import DataTable, { Column } from "../components/DataTable";
import Modal from "../components/Modal";
import { IconLoader } from "../components/Icons";

interface TenantUser {
  id: string;
  email: string;
  full_name: string | null;
  role: string;
  is_active: boolean;
  email_verified_at: string | null;
  last_login_at: string | null;
  last_activity_at: string | null;
  created_at: string;
}

const ROLES = ["owner", "manager", "accountant"];

export default function TenantUsers() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const setTenantId = useAppStore((s) => s.setTenantId);

  useEffect(() => {
    if (id) setTenantId(id);
  }, [id, setTenantId]);

  const { data: users, isLoading, error } = useQuery({
    queryKey: ["ops-tenant-users", id],
    queryFn: async () => {
      if (!id) throw new Error("No tenant id");
      const res = await api.get<TenantUser[]>(`/ops/tenants/${id}/users?limit=500`);
      return res.data;
    },
  });

  const [edit, setEdit] = useState<TenantUser | null>(null);
  const [form, setForm] = useState<Partial<TenantUser>>({});
  const [notice, setNotice] = useState<string | null>(null);

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!id || !edit) throw new Error("Missing data");
      await api.put(`/ops/tenants/${id}/users/${edit.id}`, form);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ops-tenant-users", id] });
      setEdit(null);
    },
  });

  const resetMutation = useMutation({
    mutationFn: async (userId: string) => {
      if (!id) throw new Error("No tenant id");
      await api.post(`/ops/tenants/${id}/users/${userId}/reset-password`);
    },
    onSuccess: () => setNotice(t("tenantUsers.resetSent")),
  });

  const deleteMutation = useMutation({
    mutationFn: async (userId: string) => {
      if (!id) throw new Error("No tenant id");
      await api.delete(`/ops/tenants/${id}/users/${userId}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ops-tenant-users", id] }),
  });

  function openEdit(user: TenantUser) {
    setEdit(user);
    setForm({
      email: user.email,
      full_name: user.full_name,
      role: user.role,
      is_active: user.is_active,
    });
  }

  const columns: Column<TenantUser>[] = [
    { key: "email", header: t("tenantUsers.email"), render: (r) => r.email },
    { key: "fullName", header: t("tenantUsers.fullName"), render: (r) => r.full_name ?? "—" },
    { key: "role", header: t("tenantUsers.role"), render: (r) => r.role },
    {
      key: "active",
      header: t("tenantUsers.active"),
      render: (r) => <span className={`badge ${r.is_active ? "badge-green" : "badge-red"}`}>{r.is_active ? t("common.yes") : t("common.no")}</span>,
    },
    {
      key: "verified",
      header: t("tenantUsers.verified"),
      render: (r) => <span className={`badge ${r.email_verified_at ? "badge-green" : "badge-yellow"}`}>{r.email_verified_at ? t("common.yes") : t("common.no")}</span>,
    },
    {
      key: "actions",
      header: "",
      render: (r) => (
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => openEdit(r)} className="ghost" style={{ padding: "6px 10px" }}>{t("common.edit")}</button>
          <button onClick={() => resetMutation.mutate(r.id)} className="ghost" style={{ padding: "6px 10px" }} disabled={resetMutation.isPending}>{t("tenantUsers.resetPassword")}</button>
          <button onClick={() => { if (window.confirm(t("common.confirmDelete"))) deleteMutation.mutate(r.id); }} className="danger" style={{ padding: "6px 10px" }} disabled={deleteMutation.isPending}>{t("common.delete")}</button>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return <div style={{ padding: 80, textAlign: "center" }}><IconLoader size={32} className="muted" /></div>;
  }

  return (
    <div>
      <div className="page-header">
        <h1>{t("nav.tenantUsers")}</h1>
        <p>{t("tenantUsers.subtitle")}</p>
      </div>

      {notice && (
        <div style={{ padding: 12, marginBottom: 16, background: "var(--green-dim)", color: "var(--green)", borderRadius: "var(--radius-sm)" }}>
          {notice}
        </div>
      )}

      <div className="card">
        <DataTable
          rows={users ?? []}
          columns={columns}
          loading={isLoading}
          error={error ? t("tenantUsers.loadError") : null}
          emptyMessage={t("tenantUsers.noUsers")}
        />
      </div>

      <Modal open={!!edit} onClose={() => setEdit(null)} title={t("common.edit")}>
        {edit && (
          <form
            onSubmit={(e) => { e.preventDefault(); updateMutation.mutate(); }}
            style={{ display: "flex", flexDirection: "column", gap: 16 }}
          >
            <div>
              <label>{t("tenantUsers.email")}</label>
              <input type="email" value={form.email ?? ""} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
            </div>
            <div>
              <label>{t("tenantUsers.fullName")}</label>
              <input value={form.full_name ?? ""} onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))} />
            </div>
            <div>
              <label>{t("tenantUsers.role")}</label>
              <select value={form.role ?? ""} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}>
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={form.is_active ?? false}
                onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
              />
              {t("tenantUsers.active")}
            </label>
            {updateMutation.isError && <div style={{ color: "var(--red)" }}>{t("tenantUsers.saveError")}</div>}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
              <button type="button" onClick={() => setEdit(null)} className="ghost">{t("common.cancel")}</button>
              <button type="submit" className="primary" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? <IconLoader size={16} /> : t("common.save")}
              </button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
